/**
 * Verrouillage local par code PIN (spec §12). Le code n'est jamais stocké : seule son empreinte
 * PBKDF2-SHA256 (sel aléatoire, 210 000 itérations) est gardée sur l'appareil, par compte.
 * Après plusieurs erreurs, l'attente s'allonge ; au 10ᵉ échec, il faut se reconnecter avec son
 * mot de passe (le code est alors effacé). Le PIN protège l'accès à l'appareil ; les données
 * restent protégées côté serveur par la session et la RLS.
 */

export const PIN_LENGTH = 4
const ITERATIONS = 210_000
const PREFIX = 'centime.lock.'
export const MAX_FAILURES = 10

export interface LockConfig {
  salt: string
  hash: string
  iterations: number
  /** Délai d'inactivité avant verrouillage, en minutes (0 : dès que l'app passe en arrière-plan). */
  delayMinutes: number
  failures: number
  /** Instant (ms) avant lequel aucune saisie n'est acceptée, après trop d'erreurs. */
  blockedUntil: number | null
  /** Identifiant de l'empreinte biométrique de l'appareil (WebAuthn), si activée. */
  credentialId: string | null
}

export type VerifyResult =
  | { ok: true }
  | { ok: false; reason: 'wrong'; remaining: number; blockedUntil: number | null }
  | { ok: false; reason: 'blocked'; blockedUntil: number }
  | { ok: false; reason: 'exhausted' }

function toBase64(bytes: Uint8Array): string {
  let text = ''
  for (const byte of bytes) text += String.fromCharCode(byte)
  return btoa(text)
}

function fromBase64(value: string): Uint8Array<ArrayBuffer> {
  const raw = atob(value)
  const bytes = new Uint8Array(new ArrayBuffer(raw.length))
  for (let index = 0; index < raw.length; index += 1) bytes[index] = raw.charCodeAt(index)
  return bytes
}

export function isValidPin(pin: string): boolean {
  return new RegExp(`^\\d{${String(PIN_LENGTH)}}$`).test(pin)
}

export async function hashPin(pin: string, salt: string, iterations: number): Promise<string> {
  const key = await crypto.subtle.importKey('raw', new TextEncoder().encode(pin), 'PBKDF2', false, [
    'deriveBits',
  ])
  const bits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', hash: 'SHA-256', salt: fromBase64(salt), iterations },
    key,
    256,
  )
  return toBase64(new Uint8Array(bits))
}

/** Comparaison en temps constant (pas d'indice sur le nombre de caractères justes). */
function sameHash(a: string, b: string): boolean {
  if (a.length !== b.length) return false
  let difference = 0
  for (let index = 0; index < a.length; index += 1)
    difference |= a.charCodeAt(index) ^ b.charCodeAt(index)
  return difference === 0
}

/** Attente imposée après `failures` erreurs consécutives. */
export function blockDuration(failures: number): number {
  if (failures < 5) return 0
  const minutes = [0.5, 1, 5, 15, 60][Math.min(failures - 5, 4)] ?? 60
  return minutes * 60_000
}

export function readLock(userId: string): LockConfig | null {
  try {
    const raw = localStorage.getItem(PREFIX + userId)
    if (!raw) return null
    const value = JSON.parse(raw) as Partial<LockConfig>
    if (typeof value.salt !== 'string' || typeof value.hash !== 'string') return null
    return {
      salt: value.salt,
      hash: value.hash,
      iterations: typeof value.iterations === 'number' ? value.iterations : ITERATIONS,
      delayMinutes: typeof value.delayMinutes === 'number' ? value.delayMinutes : 5,
      failures: typeof value.failures === 'number' ? value.failures : 0,
      blockedUntil: typeof value.blockedUntil === 'number' ? value.blockedUntil : null,
      credentialId: typeof value.credentialId === 'string' ? value.credentialId : null,
    }
  } catch {
    return null
  }
}

export function writeLock(userId: string, config: LockConfig): void {
  localStorage.setItem(PREFIX + userId, JSON.stringify(config))
}

export function clearLock(userId: string): void {
  try {
    localStorage.removeItem(PREFIX + userId)
  } catch {
    // Stockage indisponible : rien à effacer.
  }
}

export async function createLock(
  userId: string,
  pin: string,
  keep?: Pick<LockConfig, 'delayMinutes' | 'credentialId'>,
): Promise<LockConfig> {
  if (!isValidPin(pin)) throw new RangeError('Code PIN invalide')
  const salt = toBase64(crypto.getRandomValues(new Uint8Array(16)))
  const config: LockConfig = {
    salt,
    hash: await hashPin(pin, salt, ITERATIONS),
    iterations: ITERATIONS,
    delayMinutes: keep?.delayMinutes ?? 5,
    failures: 0,
    blockedUntil: null,
    credentialId: keep?.credentialId ?? null,
  }
  writeLock(userId, config)
  return config
}

/** Vérifie un code et met à jour le compteur d'erreurs et l'attente. */
export async function verifyPin(
  userId: string,
  pin: string,
  now = Date.now(),
): Promise<VerifyResult> {
  const config = readLock(userId)
  if (!config) return { ok: true }
  if (config.blockedUntil !== null && now < config.blockedUntil) {
    return { ok: false, reason: 'blocked', blockedUntil: config.blockedUntil }
  }
  if (sameHash(await hashPin(pin, config.salt, config.iterations), config.hash)) {
    writeLock(userId, { ...config, failures: 0, blockedUntil: null })
    return { ok: true }
  }
  const failures = config.failures + 1
  if (failures >= MAX_FAILURES) {
    clearLock(userId)
    return { ok: false, reason: 'exhausted' }
  }
  const wait = blockDuration(failures)
  const blockedUntil = wait > 0 ? now + wait : null
  writeLock(userId, { ...config, failures, blockedUntil })
  return { ok: false, reason: 'wrong', remaining: MAX_FAILURES - failures, blockedUntil }
}
